import { RenderClearFlag } from "../../../../RenderEngine/RenderEnum/RenderClearFlag";
import { Command } from "../../../../d3/core/render/command/Command";
import { Color } from "../../../../maths/Color";
import { Vector4 } from "../../../../maths/Vector4";
import { Viewport } from "../../../../maths/Viewport";
import { BlitQuadCMDData, DrawElementCMDData, DrawNodeCMDData, SetRenderTargetCMD, SetViewportCMD } from "../../../DriverDesign/3DRenderPass/IRender3DCMD";
import { RenderCMDType } from "../../../DriverDesign/RenderDevice/IRenderCMD";
export class WebGLDrawNodeCMDData extends DrawNodeCMDData {
    get node() {
        return this._node;
    }
    set node(value) {
        this._node = value;
    }
    get destShaderData() {
        return this._destShaderData;
    }
    set destShaderData(value) {
        this._destShaderData = value;
    }
    get destSubShader() {
        return this._destSubShader;
    }
    set destSubShader(value) {
        this._destSubShader = value;
    }
    get subMeshIndex() {
        return this._subMeshIndex;
    }
    set subMeshIndex(value) {
        this._subMeshIndex = value;
    }
    constructor() {
        super();
        this.type = RenderCMDType.DrawNode;
    }
    apply(context) {
        if (this.destShaderData && this.destSubShader) {
            this.node._renderUpdatePre(context);
            if (this.subMeshIndex == -1) {
                this.node.renderelements.forEach(element => {
                    let oriSubShader = element.subShader;
                    let oriMatShaderData = element.materialShaderData;
                    element.subShader = this._destSubShader;
                    element.materialShaderData = this._destShaderData;
                    context.drawRenderElementOne(element);
                    element.subShader = oriSubShader;
                    element.materialShaderData = oriMatShaderData;
                });
            }
            else {
                let element = this.node.renderelements[this.subMeshIndex];
                let oriSubShader = element.subShader;
                let oriMatShaderData = element.materialShaderData;
                element.subShader = this._destSubShader;
                element.materialShaderData = this._destShaderData;
                context.drawRenderElementOne(element);
                element.subShader = oriSubShader;
                element.materialShaderData = oriMatShaderData;
            }
        }
    }
}
export class WebGLBlitQuadCMDData extends BlitQuadCMDData {
    get dest() {
        return this._dest;
    }
    set dest(value) {
        this._dest = value;
    }
    get viewport() {
        return this._viewport;
    }
    set viewport(value) {
        value.cloneTo(this._viewport);
    }
    get scissor() {
        return this._scissor;
    }
    set scissor(value) {
        value.cloneTo(this._scissor);
    }
    get source() {
        return this._source;
    }
    set source(value) {
        this._source = value;
        if (this._source) {
            this._sourceTexelSize.setValue(1.0 / this._source.width, 1.0 / this._source.height, this._source.width, this._source.height);
        }
    }
    get offsetScale() {
        return this._offsetScale;
    }
    set offsetScale(value) {
        value.cloneTo(this._offsetScale);
    }
    get element() {
        return this._element;
    }
    set element(value) {
        this._element = value;
    }
    constructor() {
        super();
        this.type = RenderCMDType.Blit;
        this._viewport = new Viewport();
        this._scissor = new Vector4();
        this._offsetScale = new Vector4();
        this._sourceTexelSize = new Vector4();
    }
    apply(context) {
        this.element.materialShaderData._setInternalTexture(Command.SCREENTEXTURE_ID, this._source);
        this.element.materialShaderData.setVector(Command.SCREENTEXTUREOFFSETSCALE_ID, this._offsetScale);
        this.element.materialShaderData.setVector(Command.MAINTEXTURE_TEXELSIZE_ID, this._sourceTexelSize);
        context.setViewPort(this._viewport);
        context.setScissor(this._scissor);
        context.setRenderTarget(this.dest, RenderClearFlag.Nothing);
        context.drawRenderElementOne(this.element);
    }
}
export class WebGLDrawElementCMDData extends DrawElementCMDData {
    constructor() {
        super();
        this.type = RenderCMDType.DrawElement;
    }
    setRenderelements(value) {
        this._elemets = value;
    }
    apply(context) {
        if (this._elemets.length == 1) {
            context.drawRenderElementOne(this._elemets[0]);
        }
        else {
            this._elemets.forEach(element => {
                context.drawRenderElementOne(element);
            });
        }
    }
}
export class WebGLSetViewportCMD extends SetViewportCMD {
    get viewport() {
        return this._viewport;
    }
    set viewport(value) {
        this._viewport = value;
    }
    get scissor() {
        return this._scissor;
    }
    set scissor(value) {
        this._scissor = value;
    }
    constructor() {
        super();
        this.type = RenderCMDType.ChangeViewPort;
        this.scissor = new Vector4();
        this.viewport = new Viewport();
    }
    apply(context) {
        context.setViewPort(this.viewport);
        context.setScissor(this.scissor);
    }
}
const viewport = new Viewport();
const scissor = new Vector4();
export class WebGLSetRenderTargetCMD extends SetRenderTargetCMD {
    get rt() {
        return this._rt;
    }
    set rt(value) {
        this._rt = value;
    }
    get clearFlag() {
        return this._clearFlag;
    }
    set clearFlag(value) {
        this._clearFlag = value;
    }
    get clearColorValue() {
        return this._clearColorValue;
    }
    set clearColorValue(value) {
        value.cloneTo(this._clearColorValue);
    }
    get clearDepthValue() {
        return this._clearDepthValue;
    }
    set clearDepthValue(value) {
        this._clearDepthValue = value;
    }
    get clearStencilValue() {
        return this._clearStencilValue;
    }
    set clearStencilValue(value) {
        this._clearStencilValue = value;
    }
    constructor() {
        super();
        this.type = RenderCMDType.ChangeRenderTarget;
        this._clearColorValue = new Color();
    }
    apply(context) {
        context.setRenderTarget(this.rt, RenderClearFlag.Nothing);
        context.setClearData(this.clearFlag, this.clearColorValue, this.clearDepthValue, this.clearStencilValue);
        if (this.rt) {
            viewport.set(0, 0, this.rt._textures[0].width, this.rt._textures[0].height);
            scissor.setValue(0, 0, this.rt._textures[0].width, this.rt._textures[0].height);
            context.setViewPort(viewport);
            context.setScissor(scissor);
        }
    }
}
